import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import StopIcon from "@mui/icons-material/Stop";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import {
  formatPlayerStats,
  playerClassement,
  playerRankingPoints,
  resolvePlayerDisplayFields,
  rosterLookup,
} from "../../utils/liveTrainingPlayerContext";
import {
  deriveKotCoachRoundState,
  isKotWaitingAfterSubmit,
  KOT_POST_SUBMIT_PHASE_MS,
  isViewerKotLoneWaiting,
  findLonePlayerOnLastTableFromSnapshot,
  previewFullRoundRotation,
  previewRotationForMatch,
  resolveKotViewerMatchForDisplay,
  resolveViewerKotDestinationTable,
  resolveViewerKotTableMove,
} from "../../utils/kotCoachRound";
import { normalizeKotMode } from "../../utils/kotCoachSubmit";
import { KotPlayerLeaderboardList } from "./KotPlayerPanels";
import { LiveTrainingKotTitleBar } from "./LiveTrainingKotTitleBar";
import { LiveHrTrendPanel } from "../../components/LiveTraining/LiveHrTrendPanel";
import { PanelDisplayModeToggle } from "../../components/LiveTraining/PanelDisplayModeToggle";
import {
  calculateMHR,
} from "../../features/trainingSummary/zoneUtils";

function resolveArenaPlayer({
  side,
  match,
  playersById,
  roster,
  leaderboard,
  viewerPlayerId,
}) {
  const playerId = side === "A" ? match.player_a_id : match.player_b_id;
  const slot = side === "A" ? match.player_a : match.player_b;
  const embedded = slot || playersById.get(String(playerId));
  const lbIdx = Array.isArray(leaderboard)
    ? leaderboard.findIndex((r) => String(r.player_id) === String(playerId))
    : -1;
  const lbRow = lbIdx >= 0 ? leaderboard[lbIdx] : null;
  const display = resolvePlayerDisplayFields(embedded || lbRow, roster, playerId);
  const rosterEntry = rosterLookup(roster, playerId);

  return {
    side,
    playerId,
    name: display.name || (playerId ? String(playerId).slice(-6) : "—"),
    classement:
      display.classement ||
      embedded?.fltt_classement ||
      playerClassement(rosterEntry, embedded),
    rankingPoints:
      display.rankingPoints ??
      embedded?.fltt_ranking_points ??
      playerRankingPoints(rosterEntry, embedded),
    kotRank: lbIdx >= 0 ? lbIdx + 1 : null,
    kotPoints: lbRow?.total_points ?? 0,
    isViewer:
      viewerPlayerId != null &&
      playerId != null &&
      String(playerId) === String(viewerPlayerId),
    liveScore: side === "A" ? match.player_a_score : match.player_b_score,
  };
}

function resolvePhysiology(user) {
  let profiling = {};
  try {
    const raw =
      localStorage.getItem("profilingData") ||
      localStorage.getItem("playerFormData");
    if (raw) profiling = JSON.parse(raw) || {};
  } catch {
    profiling = {};
  }
  const genderRaw = profiling.gender ?? user?.gender ?? user?.sex ?? "male";
  const gender = String(genderRaw).toLowerCase() === "female" ? "female" : "male";
  let age = Number(user?.age);
  if (!Number.isFinite(age) || age <= 0) {
    const dob = profiling.dateOfBirth || user?.dateOfBirth;
    if (dob) {
      const birth = new Date(dob);
      if (!Number.isNaN(birth.getTime())) {
        const today = new Date();
        age = today.getFullYear() - birth.getFullYear();
        const m = today.getMonth() - birth.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age -= 1;
      }
    }
  }
  if (!Number.isFinite(age) || age <= 0) age = 30;
  return { age, gender };
}

function ArenaPlayerCard({
  player,
  t,
  displayMode = "default",
  onToggleDisplayMode,
  hrSamples = [],
  showHrToggle = false,
  mhr = 180,
  liveBpm = null,
  inRound = false,
}) {
  const statsLine = formatPlayerStats(
    player.classement,
    player.rankingPoints,
    t
  );

  const discreteHrMeta = [
    player.name,
    statsLine,
    player.kotRank != null
      ? t("kotPlayerArenaRank", { rank: player.kotRank }) || `#${player.kotRank}`
      : null,
    `${player.kotPoints} ${t("points")}`,
    player.liveScore != null ? String(player.liveScore) : null,
  ]
    .filter(Boolean)
    .join(" · ");

  if (
    displayMode === "hrTrend" &&
    player.isViewer &&
    showHrToggle
  ) {
    return (
      <div
        className={`lt-kot-arena__player lt-kot-arena__player--${player.side.toLowerCase()} lt-kot-arena__player--me lt-kot-arena__player--hr-trend`}
      >
        <LiveHrTrendPanel
          player={{
            name: player.name,
            classement: player.classement,
            rankingPoints: player.rankingPoints,
          }}
          samples={hrSamples}
          mhr={mhr}
          layout="arena"
          liveBpm={liveBpm}
          discreteMeta={discreteHrMeta}
          toggle={
            <PanelDisplayModeToggle
              mode={displayMode}
              onToggle={onToggleDisplayMode}
              className="lt-pv__panel-mode-toggle lt-pv__panel-mode-toggle--arena"
            />
          }
        />
      </div>
    );
  }

  return (
    <div
      className={`lt-kot-arena__player lt-kot-arena__player--${player.side.toLowerCase()}${
        player.isViewer ? " lt-kot-arena__player--me" : ""
      }`}
    >
      {player.isViewer && showHrToggle ? (
        <div className="lt-kot-arena__player-toggle">
          <PanelDisplayModeToggle
            mode={displayMode}
            onToggle={onToggleDisplayMode}
            className="lt-pv__panel-mode-toggle"
          />
        </div>
      ) : null}
      <span className="lt-kot-arena__side-badge">{player.side}</span>
      <p className="lt-kot-arena__player-name">{player.name}</p>
      {player.isViewer ? (
        <span className="lt-kot-arena__you-pill">{t("kotYou")}</span>
      ) : null}
      {statsLine ? (
        <p className="lt-kot-arena__player-level">{statsLine}</p>
      ) : null}
      <div className="lt-kot-arena__player-kot">
        {player.kotRank != null ? (
          <span className="lt-kot-arena__rank">
            {t("kotPlayerArenaRank", { rank: player.kotRank }) ||
              `#${player.kotRank}`}
          </span>
        ) : null}
        <span className="lt-kot-arena__session-pts">
          {player.kotPoints} {t("points")}
        </span>
      </div>
      {player.liveScore != null ? (
        <span className="lt-kot-arena__live-score">{player.liveScore}</span>
      ) : null}
    </div>
  );
}

function MoveDirectionIcon({ direction }) {
  if (direction === "up") {
    return <ArrowUpwardIcon className="lt-kot-arena__move-icon" aria-hidden />;
  }
  if (direction === "down") {
    return <ArrowDownwardIcon className="lt-kot-arena__move-icon" aria-hidden />;
  }
  return <SwapHorizIcon className="lt-kot-arena__move-icon" aria-hidden />;
}

/**
 * Full-screen gamified KoT match view for players at the table.
 */
export function LiveTrainingKotPlayerArena({
  match,
  snapshot,
  roster,
  viewerPlayerId,
  exerciseStateId,
  exercise = null,
  exerciseNumber = null,
  user = null,
  isLandscape = false,
  roundState,
  stoppingRound,
  onStop,
  onOpenLeaderboard,
  stopRoundError,
  showStopBanner,
  showStopButton = false,
  showLeaderboardButton = false,
  getHrSamples,
  viewerHr = null,
}) {
  const { t } = useTranslation();
  const [elapsedSeconds, setElapsedSeconds] = useState(null);
  const [waitingPhase, setWaitingPhase] = useState("rotation");
  const [panelDisplayMode, setPanelDisplayMode] = useState("default");

  const config = snapshot?.config || {};
  const kotMode = normalizeKotMode(config.match_winner_mode || config.kot_mode);
  const setTarget = Math.max(3, Number(config.set_points_target) || 11);
  const leaderboard = snapshot?.leaderboard || [];

  const coachRound = useMemo(
    () => deriveKotCoachRoundState(snapshot, exerciseStateId),
    [snapshot, exerciseStateId]
  );

  const loneWaiting = useMemo(
    () => isViewerKotLoneWaiting(snapshot, viewerPlayerId, roundState),
    [snapshot, viewerPlayerId, roundState]
  );

  const waitingAfterSubmit = useMemo(
    () => isKotWaitingAfterSubmit(snapshot, match, exerciseStateId),
    [snapshot, match, exerciseStateId]
  );

  const waitingRotation = useMemo(() => {
    if (Array.isArray(snapshot?.rotation) && snapshot.rotation.length > 0) {
      return snapshot.rotation;
    }
    const full = previewFullRoundRotation(snapshot);
    if (full.length > 0) return full;
    return previewRotationForMatch(match, snapshot);
  }, [snapshot, match]);

  useEffect(() => {
    if (!waitingAfterSubmit) {
      setWaitingPhase("rotation");
      return undefined;
    }
    setWaitingPhase("rotation");
    const id = window.setInterval(() => {
      setWaitingPhase((prev) => (prev === "rotation" ? "classement" : "rotation"));
    }, KOT_POST_SUBMIT_PHASE_MS);
    return () => window.clearInterval(id);
  }, [waitingAfterSubmit, match?.kot_match_id]);

  const tableMove = useMemo(
    () => resolveViewerKotTableMove(waitingRotation, viewerPlayerId, t),
    [waitingRotation, viewerPlayerId, t]
  );

  const destinationTable = useMemo(
    () => resolveViewerKotDestinationTable(waitingRotation, viewerPlayerId),
    [waitingRotation, viewerPlayerId]
  );

  const showWaitingRotation =
    waitingAfterSubmit && waitingPhase === "rotation";
  const showWaitingClassement =
    waitingAfterSubmit &&
    waitingPhase === "classement" &&
    leaderboard.length > 0;
  const showMovePrompt =
    !waitingAfterSubmit &&
    coachRound.awaitingCoachStart &&
    (coachRound.allTablesSubmitted ||
      coachRound.submissionSummary?.allSubmitted);
  const showWaitPanel =
    showWaitingRotation || showWaitingClassement || showMovePrompt;

  const inRound =
    !showWaitPanel &&
    (roundState === "IN_PROGRESS" || roundState === "STOP_REQUESTED");

  const displayMatch = useMemo(() => {
    if (!match) return null;
    if (!showWaitPanel) return match;
    return (
      resolveKotViewerMatchForDisplay(snapshot, viewerPlayerId, waitingRotation) ||
      match
    );
  }, [
    match,
    showWaitPanel,
    snapshot,
    viewerPlayerId,
    waitingRotation,
  ]);

  const playersById = useMemo(() => {
    const map = new Map();
    (leaderboard || []).forEach((row) => {
      if (row?.player_id) map.set(String(row.player_id), row);
    });
    if (match?.player_a_id && match.player_a) {
      map.set(String(match.player_a_id), match.player_a);
    }
    if (match?.player_b_id && match.player_b) {
      map.set(String(match.player_b_id), match.player_b);
    }
    if (displayMatch?.player_a_id && displayMatch.player_a) {
      map.set(String(displayMatch.player_a_id), displayMatch.player_a);
    }
    if (displayMatch?.player_b_id && displayMatch.player_b) {
      map.set(String(displayMatch.player_b_id), displayMatch.player_b);
    }
    (snapshot?.tables || []).forEach((tbl) => {
      (tbl.players || []).forEach((p) => {
        if (p?.player_id) map.set(String(p.player_id), p);
      });
    });
    return map;
  }, [leaderboard, match, displayMatch, snapshot?.tables]);

  const playerA = useMemo(
    () =>
      displayMatch
        ? resolveArenaPlayer({
            side: "A",
            match: displayMatch,
            playersById,
            roster,
            leaderboard,
            viewerPlayerId,
          })
        : null,
    [displayMatch, playersById, roster, leaderboard, viewerPlayerId]
  );

  const playerB = useMemo(
    () =>
      displayMatch
        ? resolveArenaPlayer({
            side: "B",
            match: displayMatch,
            playersById,
            roster,
            leaderboard,
            viewerPlayerId,
          })
        : null,
    [displayMatch, playersById, roster, leaderboard, viewerPlayerId]
  );

  useEffect(() => {
    const base = snapshot?.round_timer?.round_elapsed_seconds;
    const active =
      roundState === "IN_PROGRESS" || roundState === "STOP_REQUESTED";

    if (base == null || !active) {
      setElapsedSeconds(base != null ? Number(base) : 0);
      return undefined;
    }

    const anchorMs = Date.now() - Number(base) * 1000;
    const tick = () => {
      setElapsedSeconds(Math.max(0, Math.floor((Date.now() - anchorMs) / 1000)));
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [snapshot?.round_timer?.round_elapsed_seconds, roundState]);

  useEffect(() => {
    setPanelDisplayMode("default");
  }, [exercise?.te_id]);

  const { age, gender } = resolvePhysiology(user);
  const mhr = calculateMHR({ age, gender });

  const viewerHrSamples = useMemo(() => {
    if (!getHrSamples || !viewerPlayerId) return [];
    return getHrSamples(viewerPlayerId, user?.user_id);
  }, [getHrSamples, viewerPlayerId, user?.user_id]);

  const showHrToggle = Boolean(getHrSamples);

  const handleTogglePanelMode = () => {
    setPanelDisplayMode((prev) =>
      prev === "hrTrend" ? "default" : "hrTrend"
    );
  };

  const arenaCardProps = {
    displayMode: panelDisplayMode,
    onToggleDisplayMode: handleTogglePanelMode,
    hrSamples: viewerHrSamples,
    showHrToggle,
    mhr,
    liveBpm: viewerHr,
    inRound,
    t,
  };

  if (loneWaiting && !waitingAfterSubmit && !showWaitPanel) {
    const loneTable = findLonePlayerOnLastTableFromSnapshot(snapshot);
    return (
      <div className="lt-kot-arena lt-kot-arena--empty lt-kot-arena--lone-wait">
        <LiveTrainingKotTitleBar
          user={user}
          exercise={exercise}
          exerciseNumber={exerciseNumber}
          exerciseStateId={exerciseStateId}
          kotSnapshot={snapshot}
          tableNumber={loneTable?.table_number}
          timerSeconds={elapsedSeconds}
        />
        <div className="lt-kot-arena__lone-wait" role="status">
          <p className="lt-kot-arena__lone-wait-title">
            {t("kotLonePlayerWaitingTitle") || "Waiting for opponent"}
          </p>
          <p className="lt-kot-arena__lone-wait-hint">
            {t("kotLonePlayerWaitingHint") ||
              "You win by default when the round ends and move up one table."}
          </p>
        </div>
      </div>
    );
  }

  if (!match || !displayMatch || !playerA || !playerB) {
    return (
      <div className="lt-kot-arena lt-kot-arena--empty">
        <p>{t("kotWaitingSnapshot")}</p>
      </div>
    );
  }

  const layoutClass = isLandscape
    ? " lt-kot-arena--landscape"
    : " lt-kot-arena--portrait";

  const stopLabel =
    kotMode === "FULL_SET" && roundState !== "STOP_REQUESTED"
      ? t("liveTrainingKoTSubmit") || t("kotPlayerEnterResult")
      : stoppingRound
        ? t("kotCoachStoppingRound")
        : t("kotPlayerStopRound") || "STOP";

  return (
    <div
      className={`lt-kot-arena${layoutClass}${
        showWaitPanel ? " lt-kot-arena--post-round" : ""
      }`}
      aria-label={t("kingOfTheTable")}
    >
      <LiveTrainingKotTitleBar
        user={user}
        exercise={exercise}
        exerciseNumber={exerciseNumber}
        exerciseStateId={exerciseStateId}
        kotSnapshot={snapshot}
        tableNumber={displayMatch.table_number}
        timerSeconds={elapsedSeconds}
      />

      <div className="lt-kot-arena__body">
        {showWaitingClassement ? (
          <div className="lt-kot-arena__wait-panel lt-kot-arena__waiting-classement" role="status">
            <EmojiEventsIcon fontSize="small" />
            <div className="lt-kot-arena__waiting-classement-copy">
              <strong>{t("classement") || t("kotLeaderboard")}</strong>
              <KotPlayerLeaderboardList
                rows={leaderboard}
                matches={snapshot?.matches || []}
                myPlayerId={viewerPlayerId}
                roster={roster}
                maxRows={8}
                compact
              />
              <span className="lt-kot-arena__move-wait">
                {t("kotPlayerWaitOtherTables") ||
                  "Waiting for other tables…"}
              </span>
            </div>
          </div>
        ) : showWaitingRotation || showMovePrompt ? (
          <div className="lt-kot-arena__wait-panel lt-kot-arena__wait-panel--upcoming">
            <div
              className={`lt-kot-arena__move-banner lt-kot-arena__move-banner--${
                tableMove.direction || "generic"
              }`}
              role="status"
            >
              <MoveDirectionIcon direction={tableMove.direction} />
              <div className="lt-kot-arena__move-copy">
                <strong>
                  {tableMove.direction === "stay" && destinationTable === 1
                    ? t("kotPlayerRoundCompleteTopTable") ||
                      t("kotPlayerRoundCompleteMove")
                    : t("kotPlayerRoundCompleteMove")}
                </strong>
                {tableMove.message ? (
                  <span className="lt-kot-arena__go-table">{tableMove.message}</span>
                ) : destinationTable != null ? (
                  <span className="lt-kot-arena__go-table">
                    {t("kotPlayerGoToTable", { number: destinationTable }) ||
                      t("table_with_number", { number: destinationTable })}
                  </span>
                ) : (
                  <span>{t("kotTransitionBanner")}</span>
                )}
                <span className="lt-kot-arena__move-wait">
                  {showMovePrompt
                    ? t("kotPlayerWaitNextRound")
                    : tableMove.direction === "stay" && destinationTable === 1
                      ? t("kotPlayerWaitChallenger") ||
                        t("kotPlayerWaitOtherTables") ||
                        "Waiting for your challenger…"
                      : t("kotPlayerWaitOtherTables") ||
                        t("kotPlayerWaitNextRound") ||
                        "Waiting for other tables…"}
                </span>
              </div>
            </div>
            <div className="lt-kot-arena__match lt-kot-arena__match--preview">
              <ArenaPlayerCard player={playerA} {...arenaCardProps} />
              <div className="lt-kot-arena__vs">
                <span className="lt-kot-arena__vs-text">{t("vs") || "VS"}</span>
                <span className="lt-kot-arena__vs-target">{setTarget}</span>
              </div>
              <ArenaPlayerCard player={playerB} {...arenaCardProps} />
            </div>
          </div>
        ) : (
          <>
            {showStopBanner ? (
              <div className="lt-kot-arena__stop-banner" role="status">
                {t("kotStopRequestedBannerNoRanking") ||
                  t("kotStopRequestedBanner") ||
                  "Stop called — enter winner and scores."}
              </div>
            ) : null}

            <div className="lt-kot-arena__match">
              <ArenaPlayerCard player={playerA} {...arenaCardProps} />
              <div className="lt-kot-arena__vs">
                <span className="lt-kot-arena__vs-text">{t("vs") || "VS"}</span>
                <span className="lt-kot-arena__vs-target">{setTarget}</span>
              </div>
              <ArenaPlayerCard player={playerB} {...arenaCardProps} />
            </div>
          </>
        )}
      </div>

      <div className="lt-kot-arena__footer">
        {showLeaderboardButton && onOpenLeaderboard ? (
          <button
            type="button"
            className="lt-kot-arena__leaderboard-btn"
            onClick={onOpenLeaderboard}
          >
            <EmojiEventsIcon fontSize="small" />
            {t("kotLeaderboard")}
          </button>
        ) : null}

        <div className="lt-kot-arena__actions">
          {showStopButton && !showMovePrompt && !waitingAfterSubmit ? (
            <button
              type="button"
              className="lt-coach-bar__btn lt-coach-bar__btn--danger lt-kot-arena__stop-btn"
              disabled={stoppingRound}
              onClick={onStop}
            >
              <StopIcon fontSize="small" />
              <span>{stopLabel}</span>
            </button>
          ) : null}
        </div>
      </div>

      {stopRoundError ? (
        <p className="lt-kot-arena__error" role="alert">
          {t(stopRoundError) || stopRoundError}
        </p>
      ) : null}
    </div>
  );
}

export default LiveTrainingKotPlayerArena;
